import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// 缓存有效期：5分钟
const CACHE_DURATION = 5 * 60 * 1000;
const STORAGE_KEY = 'lookup_cache';

// 缓存项接口
interface CacheItem {
  data: any[];
  timestamp: number;
  loading: boolean;
  error: string | null;
  /**
   * 后端配置版本变了（applyServerVersions）：已挂载的 useLookup 要在后台重拉一次。
   * 和 5 分钟 TTL 过期分开：TTL 过期是惰性的（不打断正在填的表单），版本变了是数据真的改了。
   */
  versionStale?: boolean;
  /**
   * 版本过期触发的后台重拉进行中（已有数据时）。这期间不置 loading——消费方（如 PlaceGroupedPanel）
   * 照旧显示旧数据，不会在用户填表时突然变成加载态；失败也不写 error，保留旧数据。
   */
  refreshing?: boolean;
}

// 后台重拉 = 已有数据且后端版本变了：重拉期间照旧显示旧数据（不置 loading），失败保留旧数据。
const isBackgroundRefetch = (item: CacheItem | undefined): boolean =>
  !!item?.versionStale && Array.isArray(item.data) && item.data.length > 0;

// 状态接口
interface LookupState {
  cache: Record<string, CacheItem>;
}

// 检查缓存是否有效
const isCacheValid = (item: CacheItem): boolean => {
  return Date.now() - item.timestamp < CACHE_DURATION;
};

/**
 * system lookup-table 字典（useSystemLookup → /system/lookup-table/*）的 cache key 前缀。
 *
 * 后端把所有 lookup 表登记成**单一** `lookup_tables` 版本号（bundle 粒度，改任意一张表都
 * bump 同一个版本号，无法区分是哪张表变了）。FE 这边每张字典表是独立 cache key（且部分由
 * 模板配置动态决定表名，无法静态枚举），所以统一加这个前缀让它们「自我标识」—— applyServerVersions
 * 收到 `lookup_tables` 版本变化时，按前缀整批失效。详见 FE_HANDOFF_versioned_lookup_cache §6。
 */
export const SYSTEM_LOOKUP_CACHE_PREFIX = 'lookuptable:';

/**
 * 后端 CacheResource 资源名 → 前端 lookup cache key 的别名映射。
 *
 * `/system/config/versions` 的 key 形如 `<resource>:tenant:100`，applyServerVersions
 * 取 `<resource>` 后默认按同名 cache key 失效（多数 lookup 的 cache key === 资源名，
 * 如 `task_subtypes` / `groups`）。
 *
 * 需要登记别名的两类：
 * 1. catalog 下拉（useCatalog）出于命名习惯把 cache key 写成 `catalog_<kind>`（单数），
 *    与后端复数资源名（`skills` / `positions` / `service_lines`）对不上。
 * 2. 同一后端资源在 FE 有多个投影变体（不同 nameFields / 含不含 inactive）各占独立
 *    cache key，但共用后端那一个版本号 —— 版本变化时需把变体一并失效（`listings` / `users`）。
 *
 * 不映射的话版本失效永远命中不到这些 key，只能靠 5min TTL 兜底。
 */
const RESOURCE_TO_CACHE_KEYS: Record<string, string[]> = {
  skills: ['catalog_skill'],
  positions: ['catalog_position'],
  service_lines: ['catalog_service_line'],
  listings: [
    'listings',
    'listings_all',
    'listings_with_kind_group',
    'listings_with_kind_group_all',
    'listings_rental_company',
    'listings_all_rental_company',
    'listings_with_kind_group_rental_company',
    'listings_with_kind_group_all_rental_company',
    'listings_rental_company_virtual',
  ],
  users: ['users', 'users_enhanced'],
  groups: ['groups_v2'],
};

// 从 localStorage 加载缓存
const loadCacheFromStorage = (): Record<string, CacheItem> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    
    const cache = JSON.parse(stored);
    const validCache: Record<string, CacheItem> = {};
    
    // 只保留有效的缓存项
    Object.keys(cache).forEach(key => {
      const item = cache[key];
      if (item && isCacheValid(item)) {
        // 重置 loading 状态，因为页面刷新后不应该保持 loading 状态
        validCache[key] = {
          ...item,
          loading: false,
          refreshing: false
        };
      }
    });
    
    return validCache;
  } catch (error) {
    console.warn('Failed to load lookup cache from localStorage:', error);
    return {};
  }
};

// 保存缓存到 localStorage
const saveCacheToStorage = (cache: Record<string, CacheItem>) => {
  try {
    // 只保存有效的缓存项到 localStorage
    const validCache: Record<string, CacheItem> = {};
    Object.keys(cache).forEach(key => {
      const item = cache[key];
      if (item && isCacheValid(item)) {
        validCache[key] = item;
      }
    });
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(validCache));
  } catch (error) {
    console.warn('Failed to save lookup cache to localStorage:', error);
  }
};

// 初始状态 - 从 localStorage 恢复
const initialState: LookupState = {
  cache: loadCacheFromStorage(),
};

// 异步thunk用于获取lookup数据
export const fetchLookupData = createAsyncThunk(
  'lookup/fetchData',
  async ({ key, apiCall }: { key: string; apiCall: () => Promise<any> }) => {
    const response = await apiCall();
    return { key, data: response };
  }
);

// 创建slice
const lookupSlice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    // 清除特定key的缓存
    clearCache: (state, action: PayloadAction<string>) => {
      delete state.cache[action.payload];
      saveCacheToStorage(state.cache);
    },
    // 清除所有缓存
    clearAllCache: (state) => {
      state.cache = {};
      saveCacheToStorage(state.cache);
    },
    // 清除过期缓存
    clearExpiredCache: (state) => {
      Object.keys(state.cache).forEach(key => {
        if (!isCacheValid(state.cache[key])) {
          delete state.cache[key];
        }
      });
      saveCacheToStorage(state.cache);
    },
    /**
     * Bootstrap 灌注：从 IndexedDB cacheStore 读出的 entries 写入 Redux。
     * Payload: `[{ key, value }, ...]` — key 是 lookup 资源名（如 "listings"），
     * value 是该资源数据 array。设置 timestamp = now 让缓存被视为有效。
     *
     * 详见 OpenSpec add-version-based-cache。
     */
    hydrateFromCache: (state, action: PayloadAction<Array<{ key: string; value: unknown }>>) => {
      const now = Date.now();
      for (const entry of action.payload) {
        // 只接受 array shape 的 lookup 数据；其他 shape 忽略避免污染
        const value = entry.value as unknown;
        if (Array.isArray(value)) {
          state.cache[entry.key] = {
            data: value as any[],
            timestamp: now,
            loading: false,
            error: null,
          };
        }
      }
    },
    /**
     * 接收 GET /system/config/versions 的返回，对比本地 prev 与服务端 next。
     * 不一致或新增的资源：把对应 Redux 缓存的 timestamp 重置为 0 → 下次 useLookup
     * 调用时 selectIsCacheValid 返回 false → 触发 refetch。
     *
     * 这是版本号驱动失效与现有 TTL 缓存协调的桥接策略：不需要改 useLookup 的本地
     * 5min TTL 逻辑就能让版本号生效。
     */
    applyServerVersions: (
      state,
      action: PayloadAction<{
        prev: Record<string, string>;
        next: Record<string, string>;
      }>
    ) => {
      const { prev, next } = action.payload;
      // 对每个版本号 key（"listings:tenant:100" 等），提取 resource 名（"listings"）
      // 然后失效 Redux 中对应 cache key 的缓存。多数资源 cache key === 资源名；
      // catalog 等命名不一致的走 RESOURCE_TO_CACHE_KEYS 别名表。
      for (const versionKey of Object.keys(next)) {
        if (prev[versionKey] !== next[versionKey]) {
          const resource = versionKey.split(':')[0];
          if (!resource) continue;
          // lookup_tables 是 bundle 资源：一个版本号覆盖全部字典表。无法判断是哪张表变了，
          // 也无法静态枚举字典名（部分模板动态决定）—— 按前缀把所有 system lookup-table
          // cache key 整批失效。
          if (resource === 'lookup_tables') {
            for (const cacheKey of Object.keys(state.cache)) {
              if (cacheKey.startsWith(SYSTEM_LOOKUP_CACHE_PREFIX)) {
                state.cache[cacheKey].timestamp = 0;
                state.cache[cacheKey].versionStale = true;
              }
            }
            continue;
          }
          const cacheKeys = RESOURCE_TO_CACHE_KEYS[resource] ?? [resource];
          for (const cacheKey of cacheKeys) {
            if (state.cache[cacheKey]) {
              state.cache[cacheKey].timestamp = 0;
              state.cache[cacheKey].versionStale = true;
            }
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLookupData.pending, (state, action) => {
        const { key } = action.meta.arg;
        if (isBackgroundRefetch(state.cache[key])) {
          // 后台重拉：不置 loading、不动 error，旧数据照旧显示
          state.cache[key].refreshing = true;
          return;
        }
        if (!state.cache[key]) {
          state.cache[key] = {
            data: [],
            timestamp: 0,
            loading: false,
            error: null
          };
        }
        state.cache[key].loading = true;
        state.cache[key].error = null;
      })
      .addCase(fetchLookupData.fulfilled, (state, action) => {
        // 防止 payload 为 undefined 的情况
        if (!action.payload) {
          console.warn('fetchLookupData fulfilled with undefined payload');
          return;
        }
        const { key, data } = action.payload;
        state.cache[key] = {
          data: data || [],
          timestamp: Date.now(),
          loading: false,
          error: null
        };
        saveCacheToStorage(state.cache);
      })
      .addCase(fetchLookupData.rejected, (state, action) => {
        const { key } = action.meta.arg;
        const item = state.cache[key];
        if (item) {
          if (item.refreshing) {
            // 后台重拉失败：不写 error，保留旧数据；versionStale 保持 true，
            // useLookup 的 staleFetchTriedRef 保证每轮只试一次、不连环重试。
            item.refreshing = false;
            item.loading = false;
            return;
          }
          item.loading = false;
          item.error = action.error.message || 'Failed to fetch lookup data';
        }
      });
  }
});

// 导出actions
export const {
  clearCache,
  clearAllCache,
  clearExpiredCache,
  hydrateFromCache,
  applyServerVersions,
} = lookupSlice.actions;

// 导出reducer
export default lookupSlice.reducer;

// 选择器 - 返回缓存数据（即使过期也返回，让调用方决定是否刷新）
export const selectLookupData = (state: { lookup: LookupState }, key: string) => {
  // 检查 state 和 cache 是否存在
  if (!state?.lookup?.cache) {
    return null;
  }

  const item = state.lookup.cache[key];
  // 只要有数据就返回，不管是否过期（过期数据仍可用，只是需要在后台刷新）
  return item?.data || null;
};

// 检查缓存是否有效（未过期）
export const selectIsCacheValid = (state: { lookup: LookupState }, key: string) => {
  if (!state?.lookup?.cache) {
    return false;
  }

  const item = state.lookup.cache[key];
  return item ? isCacheValid(item) : false;
};

// 后端配置版本变了、还没重拉（useLookup 据此在后台重拉）
export const selectIsVersionStale = (state: { lookup: LookupState }, key: string) =>
  !!state?.lookup?.cache?.[key]?.versionStale;

// 检查是否有缓存数据（不管是否过期）
export const selectHasCacheData = (state: { lookup: LookupState }, key: string) => {
  if (!state?.lookup?.cache) {
    return false;
  }

  const item = state.lookup.cache[key];
  return item && Array.isArray(item.data) && item.data.length > 0;
};

export const selectLookupLoading = (state: { lookup: LookupState }, key: string) => {
  // 检查 state 和 cache 是否存在
  if (!state?.lookup?.cache) {
    return false;
  }
  
  return state.lookup.cache[key]?.loading || false;
};

export const selectLookupError = (state: { lookup: LookupState }, key: string) => {
  // 检查 state 和 cache 是否存在
  if (!state?.lookup?.cache) {
    return null;
  }
  
  return state.lookup.cache[key]?.error || null;
};

export const selectCacheState = (state: { lookup: LookupState }) => {
  return state.lookup.cache;
};